import React from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';

import {
  APIClientCreateOrRotateResponse,
  APIClientRecord,
  useCreateApiClientMutation,
  useDeleteApiClientMutation,
  useFetchApiClientRoleOptions,
  useFetchApiClients,
  useRotateApiClientSecretMutation,
  useUpdateApiClientMutation,
} from '../../../hooks/api-client-hooks';

import { useAdminPermissions } from '../Hooks/useAdminPermissions';

import APIClientEditorDialog from './APIClientEditorDialog';
import APIClientSecretResultDialog from './APIClientSecretResultDialog';
import APIClientsTable from './APIClientsTable';

import {
  APIClientFormState,
  clientMatchesSearch,
  DialogMode,
  normalizeText,
} from './apiClientUtils';

const APIClientsAdminPage: React.FC = () => {
  const {
    hasPerm,
    isAdminFull,
  } = useAdminPermissions();

  const canRead = isAdminFull || hasPerm('api.api-client.read');
  const canCreate = isAdminFull || hasPerm('api.api-client.create');
  const canUpdate = isAdminFull || hasPerm('api.api-client.update');
  const canDelete = isAdminFull || hasPerm('api.api-client.delete');

  const clientsQuery = useFetchApiClients({
    enabled: canRead,
  });

  const rolesQuery = useFetchApiClientRoleOptions({
    enabled: canRead || canCreate || canUpdate,
  });

  const createMutation = useCreateApiClientMutation();
  const updateMutation = useUpdateApiClientMutation();
  const rotateMutation = useRotateApiClientSecretMutation();
  const deleteMutation = useDeleteApiClientMutation();

  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogMode, setDialogMode] = React.useState<DialogMode>('create');
  const [selectedClient, setSelectedClient] = React.useState<APIClientRecord | null>(null);

  const [secretResult, setSecretResult] =
    React.useState<APIClientCreateOrRotateResponse | null>(null);

  const [actionError, setActionError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setPage(0);
  }, [search]);

  const roleOptions = React.useMemo(() => {
    return Array.isArray(rolesQuery.data) ? rolesQuery.data : [];
  }, [rolesQuery.data]);

  const clients = React.useMemo(() => {
    return Array.isArray(clientsQuery.data) ? clientsQuery.data : [];
  }, [clientsQuery.data]);

  const roleNameById = React.useMemo(() => {
    const map = new Map<number, string>();

    roleOptions.forEach((role) => {
      map.set(Number(role.id), role.name);
    });

    return map;
  }, [roleOptions]);

  const filteredClients = React.useMemo(() => {
    return clients.filter((client) => clientMatchesSearch(client, search));
  }, [clients, search]);

  const pagedClients = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredClients.slice(start, start + rowsPerPage);
  }, [filteredClients, page, rowsPerPage]);

  const busy =
    clientsQuery.isLoading ||
    clientsQuery.isFetching ||
    createMutation.isPending ||
    updateMutation.isPending ||
    rotateMutation.isPending ||
    deleteMutation.isPending;

  const openCreate = () => {
    setSelectedClient(null);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openEdit = (client: APIClientRecord) => {
    setSelectedClient(client);
    setDialogMode(canUpdate ? 'edit' : 'view');
    setDialogOpen(true);
  };

  const openView = (client: APIClientRecord) => {
    setSelectedClient(client);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogMode('create');
    setSelectedClient(null);
  };

  const refresh = async () => {
    setActionError(null);

    await Promise.all([
      clientsQuery.refetch(),
      rolesQuery.refetch(),
    ]);
  };

  const saveClient = async (form: APIClientFormState) => {
    setActionError(null);

    if (dialogMode === 'create') {
      const created = await createMutation.mutateAsync({
        name: normalizeText(form.name),
        description: normalizeText(form.description) || null,
        role_ids: form.role_ids || [],
        token_ttl_seconds: Number(form.token_ttl_seconds || 3600),
      });

      closeDialog();
      setSecretResult(created);
      return;
    }

    if (!selectedClient?.client_id) {
      throw new Error('No API client selected.');
    }

    await updateMutation.mutateAsync({
      client_id: selectedClient.client_id,
      name: normalizeText(form.name),
      description: normalizeText(form.description) || null,
      enabled: Boolean(form.enabled),
      role_ids: form.role_ids || [],
      token_ttl_seconds: Number(form.token_ttl_seconds || 3600),
    });

    closeDialog();
  };

  const rotateSecret = async (client: APIClientRecord) => {
    if (!canUpdate) return;

    const confirmed = window.confirm(
      `Rotate the secret for "${client.name}"?\n\nThe old secret will stop working after rotation.`,
    );

    if (!confirmed) return;

    setActionError(null);

    try {
      const result = await rotateMutation.mutateAsync(client.client_id);
      setSecretResult(result);
    } catch (err: any) {
      setActionError(err?.message || 'Failed to rotate API client secret.');
    }
  };

  const removeClient = async (client: APIClientRecord) => {
    if (!canDelete) return;

    const confirmed = window.confirm(
      `Delete API client "${client.name}"?\n\nThis action cannot be undone.`,
    );

    if (!confirmed) return;

    setActionError(null);

    try {
      await deleteMutation.mutateAsync(client.client_id);
    } catch (err: any) {
      setActionError(err?.message || 'Failed to delete API client.');
    }
  };

  if (!canRead) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Alert severity="warning">
          API client management requires `api.api-client.read`.
        </Alert>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Stack spacing={0.5}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <KeyIcon color="primary" />

              <Typography variant="h6" fontWeight={700}>
                API Clients
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Manage service principals, assigned roles, client secrets, and token TTLs. Results are scoped to API clients you own or that are owned by users in your leadership chain.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refresh}
              disabled={busy}
            >
              Refresh
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              disabled={!canCreate || busy}
            >
              Create API Client
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {actionError ? (
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      {clientsQuery.error ? (
        <Alert severity="error">
          {clientsQuery.error.message || 'Failed to load API clients.'}
        </Alert>
      ) : null}

      {rolesQuery.error ? (
        <Alert severity="warning">
          {rolesQuery.error.message || 'Failed to load role options.'}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          mb={2}
        >
          <TextField
            size="small"
            label="Search API clients"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ minWidth: { xs: '100%', md: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={`${filteredClients.length} shown`} />
            <Chip size="small" variant="outlined" label={`${clients.length} total`} />

            {busy ? (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                icon={<CircularProgress size={12} />}
                label="Loading"
              />
            ) : null}
          </Stack>
        </Stack>

        <APIClientsTable
          rows={pagedClients}
          loading={clientsQuery.isLoading}
          canUpdate={canUpdate}
          canDelete={canDelete}
          roleNameById={roleNameById}
          rotateLoading={rotateMutation.isPending}
          deleteLoading={deleteMutation.isPending}
          onEdit={openEdit}
          onView={openView}
          onRotateSecret={rotateSecret}
          onDelete={removeClient}
        />

        <TablePagination
          component="div"
          count={filteredClients.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
        />
      </Paper>

      <APIClientEditorDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValue={selectedClient}
        roleOptions={roleOptions}
        saving={createMutation.isPending || updateMutation.isPending}
        onClose={closeDialog}
        onSubmit={saveClient}
      />

      <APIClientSecretResultDialog
        open={!!secretResult}
        result={secretResult}
        onClose={() => setSecretResult(null)}
      />
    </Stack>
  );
};

export default APIClientsAdminPage;